import React, {
  useRef,
  useEffect,
  useState,
  ReactNode,
  HTMLAttributes,
} from 'react';
import clsx from 'clsx';

interface ScrollableWithGradientProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  className?: string;
  maxHeight?: string | number;
  gradientHeight?: number;
  gradientColor?: string;
}

export function ScrollableWithGradient({
  children,
  className,
  maxHeight,
  gradientHeight = 40,
  gradientColor = '#ffffff',
  style,
  ...props
}: ScrollableWithGradientProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showGradient, setShowGradient] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const checkScrollState = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const hasScrollableContent = scrollHeight > clientHeight;
    const isNotAtBottom = scrollTop + clientHeight < scrollHeight - 1; // -1 for rounding errors

    setIsScrollable(hasScrollableContent);
    setShowGradient(hasScrollableContent && isNotAtBottom);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    // Initial check with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      checkScrollState();
    }, 100);

    // Check on scroll
    container.addEventListener('scroll', checkScrollState, { passive: true });

    // Check on resize (content might change)
    const resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to ensure accurate measurements
      requestAnimationFrame(() => {
        checkScrollState();
      });
    });

    resizeObserver.observe(container);

    // Also observe children changes
    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        checkScrollState();
      });
    });

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    return () => {
      clearTimeout(timeoutId);
      container.removeEventListener('scroll', checkScrollState);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [children]);

  const containerStyle: React.CSSProperties = {
    ...(maxHeight && { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }),
    '--scrollable-gradient-height': `${gradientHeight}px`,
    '--scrollable-gradient-color': gradientColor,
    ...style,
  } as React.CSSProperties;

  const overlayStyle: React.CSSProperties = {
    opacity: showGradient ? 1 : 0,
  };

  return (
    <div
      ref={containerRef}
      className={clsx('scrollable-with-gradient', className)}
      style={containerStyle}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    >
      <div className="scrollable-with-gradient__content">
        {children}
      </div>
      {isScrollable && (
        <div
          className="scrollable-with-gradient__overlay"
          style={overlayStyle}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
